import { useState, useEffect, useRef } from 'react';
// eslint-disable-next-line no-unused-vars
import { isIOS, isAndroid } from '../utils/pwaUtils';
// eslint-disable-next-line no-unused-vars
import { isTelegramBrowser, isInstagramBrowser } from '../utils/browserDetection';
import { detectOS, detectBrowserWithRetry, checkTelegramAPI, checkTelegramAdditional } from '../utils/deviceDetection';
import './TestDevicePage.css';

export default function TestDevicePage() {
  const [osName, setOsName] = useState('Не определено');
  const [browserName, setBrowserName] = useState('Не определено');
  const [debugInfo, setDebugInfo] = useState(null);
  const isTelegramCheckedRef = useRef(false);

  useEffect(() => {
    // Первоначальное определение
    const initialBrowser = detectBrowserWithRetry();
    setOsName(detectOS());
    setBrowserName(initialBrowser);

    // Сохраняем отладочную информацию для Telegram
    if (typeof window !== 'undefined') {
      const debug = {
        hasTelegramAPI: !!(window.Telegram && window.Telegram.WebApp),
        hasTelegramObject: !!window.Telegram,
        hasTelegramWebApp: !!window.TelegramWebApp,
        hasTgWebApp: !!window.tgWebApp,
        hasTelegramWebviewProxy: !!window.TelegramWebviewProxy,
        hasTelegramWebview: !!window.TelegramWebview,
        telegramWebAppVersion: window.Telegram?.WebApp?.version || null,
        telegramWebAppPlatform: window.Telegram?.WebApp?.platform || null,
        telegramWebAppIsExpanded: window.Telegram?.WebApp?.isExpanded ?? null,
        telegramWebAppInitData: window.Telegram?.WebApp?.initData ? 'present' : null,
        checkTelegramAPI: checkTelegramAPI(),
        checkTelegramAdditional: checkTelegramAdditional(),
        hasReadyMethod: typeof window.Telegram?.WebApp?.ready === 'function',
        hasExpandMethod: typeof window.Telegram?.WebApp?.expand === 'function',
        hasCloseMethod: typeof window.Telegram?.WebApp?.close === 'function',
      };
      setDebugInfo(debug);
    }

    // Функция для проверки и обновления Telegram
    const checkAndUpdateTelegram = () => {
      if (typeof window === 'undefined' || isTelegramCheckedRef.current) return;

      // Проверяем как основной API, так и дополнительные признаки
      if (checkTelegramAPI() || checkTelegramAdditional()) {
        isTelegramCheckedRef.current = true;
        setBrowserName('Telegram WebView');

        // Обновляем отладочную информацию
        if (window.Telegram && window.Telegram.WebApp) {
          setDebugInfo(prev => ({
            ...(prev || {}),
            checkTelegramAPI: true,
            checkTelegramAdditional: true,
            hasTelegramAPI: true,
            telegramWebAppVersion: window.Telegram.WebApp.version || null,
            telegramWebAppPlatform: window.Telegram.WebApp.platform || null,
          }));
        } else {
          setDebugInfo(prev => ({
            ...(prev || {}),
            checkTelegramAdditional: true,
            hasTelegramObject: !!window.Telegram,
          }));
        }
      }
    };

    // Повторная проверка через небольшую задержку (Telegram API может загружаться асинхронно)
    const retryTimeout = setTimeout(() => {
      checkAndUpdateTelegram();
    }, 100);

    // Повторная проверка через среднюю задержку
    const retryTimeoutMedium = setTimeout(() => {
      checkAndUpdateTelegram();
    }, 300);

    // Повторная проверка через большую задержку (на случай очень медленной загрузки)
    const retryTimeoutLong = setTimeout(() => {
      checkAndUpdateTelegram();
    }, 1000);

    // Периодическая проверка через интервал (на случай очень медленной загрузки API)
    const intervalId = setInterval(() => {
      checkAndUpdateTelegram();
      // Если Telegram обнаружен, прекращаем проверку
      if (isTelegramCheckedRef.current) {
        clearInterval(intervalId);
      }
    }, 200);

    // Прекращаем периодическую проверку через 5 секунд (если Telegram не обнаружен)
    const stopIntervalTimeout = setTimeout(() => {
      clearInterval(intervalId);
    }, 5000);

    // Слушаем события загрузки страницы для повторной проверки
    if (typeof window !== 'undefined') {
      const handleLoad = () => {
        checkAndUpdateTelegram();
      };

      if (document.readyState === 'complete') {
        handleLoad();
      } else {
        window.addEventListener('load', handleLoad);
        // Также проверяем при DOMContentLoaded
        document.addEventListener('DOMContentLoaded', handleLoad);

        return () => {
          window.removeEventListener('load', handleLoad);
          document.removeEventListener('DOMContentLoaded', handleLoad);
          clearTimeout(retryTimeout);
          clearTimeout(retryTimeoutMedium);
          clearTimeout(retryTimeoutLong);
          clearTimeout(stopIntervalTimeout);
          clearInterval(intervalId);
        };
      }
    }

    return () => {
      clearTimeout(retryTimeout);
      clearTimeout(retryTimeoutMedium);
      clearTimeout(retryTimeoutLong);
      clearTimeout(stopIntervalTimeout);
      clearInterval(intervalId);
    };
  }, []); // Пустой массив зависимостей - запускаем только один раз

  return (
    <div className="test-device-page">
      <div className="test-device-content">
        <h1 className="test-device-title">Информация об устройстве</h1>

        <div className="test-device-info">
          <div className="test-device-item">
            <span className="test-device-label">Операционная система:</span>
            <span className="test-device-value">{osName}</span>
          </div>

          <div className="test-device-item">
            <span className="test-device-label">Браузер:</span>
            <span className="test-device-value">{browserName}</span>
          </div>
        </div>

        <div className="test-device-raw-info">
          <h2 className="test-device-subtitle">Техническая информация</h2>
          <div className="test-device-user-agent">
            <span className="test-device-label">User Agent:</span>
            <pre className="test-device-pre">{navigator.userAgent}</pre>
          </div>

          {/* Отладочная информация для Telegram */}
          {debugInfo && (
            <div className="test-device-user-agent" style={{ marginTop: '16px' }}>
              <span className="test-device-label">Отладка Telegram WebApp API:</span>
              <pre className="test-device-pre" style={{ fontSize: '11px' }}>
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

